import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import en from 'src/constants/en'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { analyticsApi } from 'src/services/analyticsApi'

export type InboxSentiment = 'positive' | 'neutral' | 'negative'

export interface ChangeInboxSentimentRequest {
  conversationIds: string[]
  sentiment: InboxSentiment
}

export interface ChangeInboxSentimentResponse {
  conversationIds?: string[]
  error?: string
  modified?: number
  statsAdjusted?: number
  statsSkipped?: number
  success: boolean
}

interface UseChangeInboxSentimentParams {
  onError?: () => void
  onSuccess?: (data: ChangeInboxSentimentResponse) => void
}

const INVALIDATION_KEYS: QueryKey[] = [
  QueryKey.GET_INBOX_OVERVIEW,
  QueryKey.GET_INBOX_PREVIEW_CONVERSATIONS,
  QueryKey.GET_INBOX_PREVIEW_CONVERSATION_MESSAGES,
  QueryKey.GET_INBOX_PREVIEW_SUMMARY,
  QueryKey.GET_INBOX_SMART_INSIGHTS,
  QueryKey.GET_FREQUENTLY_USED_KEYWORDS,
  QueryKey.GET_MESSAGE_VOLUME_TRENDS,
  QueryKey.GET_OPERATOR_ANALYTICS,
  QueryKey.GET_OPERATOR_PERFORMANCE,
  QueryKey.GET_TOP_INTENTS,
  QueryKey.GET_TOP_INTENT_MESSAGES,
  QueryKey.GET_TOP_PHRASES,
]

export const useChangeInboxSentimentMutation = ({
  onError,
  onSuccess,
}: UseChangeInboxSentimentParams = {}) => {
  const queryClient = useQueryClient()

  return useMutation<
    ChangeInboxSentimentResponse,
    unknown,
    ChangeInboxSentimentRequest
  >({
    mutationFn: async ({ conversationIds, sentiment }) => {
      const { data } = await analyticsApi().post<ChangeInboxSentimentResponse>(
        '/v1_private/inbox-analysis/change-sentiment',
        { conversationIds, sentiment }
      )
      return data
    },
    onError: () => {
      toast.error(en.CHANGE_SENTIMENT_TYPE_ERROR_MESSAGE)
      onError?.()
    },
    onSuccess: (data) => {
      toast.success(en.CHANGE_SENTIMENT_TYPE_SUCCESS_MESSAGE)
      INVALIDATION_KEYS.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] })
      })
      onSuccess?.(data)
    },
  })
}
